import { useId } from 'react'
import { useController, type Control, type FieldPath, type FieldValues } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import type { Key } from 'react-aria-components'
import { ButtonGroup, ButtonGroupItem } from '@/components/base/button-group/button-group'
import { cn } from '@/lib/utils'

export interface FormSegmentedSelectProps<TFieldValues extends FieldValues> {
  control: Control<TFieldValues>
  name: FieldPath<TFieldValues>
  /** Visible field label. */
  label: string
  options: { value: string; label: string }[]
  isDisabled?: boolean
  className?: string
}

/**
 * FormSegmentedSelect - a react-hook-form integrated segmented control for a
 * small set of mutually exclusive options (e.g. "Included | Premium").
 *
 * Unlike FormScoreSelect there is no empty state: the field always holds one
 * of the option values, so re-clicking the selected segment keeps it selected.
 */
export function FormSegmentedSelect<TFieldValues extends FieldValues>({
  control,
  name,
  label,
  options,
  isDisabled,
  className,
}: FormSegmentedSelectProps<TFieldValues>) {
  const { t } = useTranslation()
  const labelId = useId()
  const {
    field,
    fieldState: { error },
  } = useController({ control, name })

  const handleSelectionChange = (keys: Set<Key>) => {
    const [key] = keys
    if (key != null) field.onChange(String(key))
  }

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <span id={labelId} className="text-sm font-medium text-gray-700 dark:text-gray-200">
        {label}
      </span>
      <ButtonGroup
        size="sm"
        aria-labelledby={labelId}
        selectedKeys={typeof field.value === 'string' && field.value ? [field.value] : []}
        onSelectionChange={handleSelectionChange}
        isDisabled={isDisabled}
        className="flex w-full max-w-xs"
      >
        {options.map((option) => {
          const isSelected = field.value === option.value
          return (
            <ButtonGroupItem
              key={option.value}
              id={option.value}
              className={cn(
                'flex-1 justify-center',
                // Match FormScoreSelect: the base selected style is too subtle
                // to read as a state change, so fill the active segment.
                isSelected &&
                  'bg-brand-500 selected:bg-brand-500 selected:text-white hover:bg-brand-600 dark:bg-brand-500 dark:hover:bg-brand-400 text-white hover:text-white'
              )}
            >
              {option.label}
            </ButtonGroupItem>
          )
        })}
      </ButtonGroup>
      {error?.message && (
        <p className="text-error-600 dark:text-error-400 text-sm">{t(error.message)}</p>
      )}
    </div>
  )
}

export default FormSegmentedSelect
